'use client';

import { useEffect } from 'react';
import { ITINERARY_ITEM_ID_PREFIX, useOverviewProgressStore } from '~/stores/tpv2/overviewProgressStore';

export const ITINERARY_ACTIVE_LINE_PX = 120;
export const ITINERARY_SECTION_ID = 'overview-itinerary';

export const ITINERARY_FOCUS_LINE_RATIO = 0.4;

export function useItineraryScrollProgress(deps: ReadonlyArray<unknown>, sectionId = ITINERARY_SECTION_ID): void {
  const setProgress = useOverviewProgressStore((s) => s.setProgress);
  const setActiveItemId = useOverviewProgressStore((s) => s.setActiveItemId);
  const overlayOpen = useOverviewProgressStore((s) => s.overlayOpen);

  useEffect(() => {
    if (globalThis.window === undefined) return;
    if (overlayOpen) return;

    let rafId = 0;
    let lastPublished: number | null = null;
    let lastActiveId: string | null = null;

    const compute = () => {
      rafId = 0;
      const section = document.getElementById(sectionId);
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const scrolledIntoSection = ITINERARY_ACTIVE_LINE_PX - rect.top;
      const progress = Math.max(0, Math.min(1, scrolledIntoSection / rect.height));

      if (lastPublished === null || Math.abs(progress - lastPublished) > 0.001) {
        lastPublished = progress;
        setProgress(progress);
      }

      const focusLine = Math.max(ITINERARY_ACTIVE_LINE_PX, window.innerHeight * ITINERARY_FOCUS_LINE_RATIO);
      const cards = section.querySelectorAll<HTMLElement>(`[id^="${ITINERARY_ITEM_ID_PREFIX}"]`);
      let activeId: string | null = null;
      for (const card of cards) {
        if (card.getBoundingClientRect().top > focusLine) break;
        activeId = card.id.slice(ITINERARY_ITEM_ID_PREFIX.length);
      }

      if (activeId !== lastActiveId) {
        lastActiveId = activeId;
        setActiveItemId(activeId);
      }
    };

    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(compute);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    schedule();

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [...deps, overlayOpen, sectionId]);
}
